// =====================================================================
// ErrorBoundary, plain React class component.
// ---------------------------------------------------------------------
// Replaces <Sentry.ErrorBoundary> so the Sentry SDK can stay out of the
// initial bundle (~200 KB on a page load that almost never uses it).
//
// If Sentry boots lazily via `import("./lib/sentry.js")` in main.jsx,
// it sets window.__lmSentry__ to the SDK module on success. This
// boundary forwards caught errors to that handle if it's available,
// otherwise just logs to console and renders the fallback.
// =====================================================================

import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);

    const Sentry = typeof window !== "undefined" ? window.__lmSentry__ : null;
    if (Sentry?.captureException) {
      try {
        Sentry.captureException(error, {
          contexts: { react: { componentStack: errorInfo?.componentStack } },
        });
      } catch (_) {
        /* never let logging itself blow up */
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="boot-screen" style={{ flexDirection: "column", gap: 12 }}>
            <strong style={{ fontFamily: "Newsreader, serif", fontSize: 24 }}>
              Something went wrong.
            </strong>
            <a
              href="/"
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12,
                color: "var(--ink-2)",
                textDecoration: "underline",
              }}
            >
              Reload the home page →
            </a>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
